import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  inventoryCounts,
  type InventoryCountItem,
} from '@/api/inventoryCounts';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  inventory_date: string;
  comment: string;
  items: InventoryCountItem[];
}

const today = new Date().toISOString().slice(0, 10);

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  inventory_date: today,
  comment: '',
  items: [],
};

const newRow = (n: number): InventoryCountItem => ({
  line_number: n,
  nomenclature_id: '',
  fact_quantity: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
const fmtMoney = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InventoryCountEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по warehouse.inventory.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('warehouse.inventory.manage');
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const docQuery = useQuery({
    queryKey: ['inventory-count', id],
    queryFn: () => inventoryCounts.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.inventory_counts', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_id: d.warehouse_id,
        inventory_date: d.inventory_date.slice(0, 10),
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id ?? null,
          series_id: it.series_id ?? null,
          book_quantity: it.book_quantity,
          fact_quantity: it.fact_quantity,
          variance_quantity: it.variance_quantity,
          book_amount: it.book_amount,
          fact_amount: it.fact_amount,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const create = useMutation({
    mutationFn: () => inventoryCounts.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_id: form.warehouse_id,
      inventory_date: form.inventory_date,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      navigate(`/inventory-counts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => inventoryCounts.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_id: form.warehouse_id,
      inventory_date: form.inventory_date,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory-count', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const recalc = useMutation({
    mutationFn: () => inventoryCounts.recalcBook(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory-count', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await inventoryCounts.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id,
        inventory_date: form.inventory_date,
        comment: form.comment || null,
        items: form.items,
      });
      await inventoryCounts.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      navigate(`/inventory-counts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await inventoryCounts.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id,
        inventory_date: form.inventory_date,
        comment: form.comment || null,
        items: form.items,
      });
      await inventoryCounts.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-count', id] });
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => inventoryCounts.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-count', id] });
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => inventoryCounts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-counts'] });
      navigate('/inventory-counts');
    },
  });

  function setItem(i: number, patch: Partial<InventoryCountItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має warehouse.inventory.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати інвентаризацію у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/inventory-counts')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова інвентаризація' : `Інвентаризація: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {/* «Заповнити з обліку» — doc-специфічна кнопка, поза DocumentToolbar. */}
          {!isNew && !isPosted && (
            <button
              onClick={() => recalc.mutate()}
              disabled={recalc.isPending}
              title="Заповнити book_quantity і book_amount з регістрів на дату інвентаризації"
            >
              Заповнити з обліку
            </button>
          )}
          <DocumentToolbar
            onBackToList={() => navigate('/inventory-counts')}
            onSave={() => isNew ? create.mutate() : update.mutate()}
            saveDisabled={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            postDisabled={!isNew && form.items.length === 0}
            onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
            postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
            postTitle="Зберігає поточні зміни та проводить документ"
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
            deletePending={remove.isPending}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>Дата інвентаризації</label>
          <DatePopover
            value={form.inventory_date}
            onChange={(v) => setForm({ ...form, inventory_date: v })}
            disabled={isPosted}
          />
        </div>
      </div>

      <div className="field">
        <label>Склад</label>
        <select
          value={form.warehouse_id}
          onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
          disabled={isPosted}
        >
          <option value="">—</option>
          {warehousesQ.data?.map((w) => (
            <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>Коментар</label>
        <input
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          disabled={isPosted}
        />
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Рядки</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Номенклатура</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Облік (книжно)</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Факт</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Відхилення</th>
            <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Сума обліку</th>
            {isPosted && <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Сума факту</th>}
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const variance = Number(it.variance_quantity ?? 0);
            const varBg = variance > 0 ? '#dcfce7' : variance < 0 ? '#fee2e2' : undefined;
            return (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem' }}>
                  <select
                    value={it.nomenclature_id}
                    onChange={(e) => setItem(i, { nomenclature_id: e.target.value })}
                    disabled={isPosted}
                  >
                    <option value="">—</option>
                    {nomList.data?.items.map((n) => (
                      <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>
                  {fmt(Number(it.book_quantity ?? 0))}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number" step="0.001" value={it.fact_quantity}
                    onChange={(e) => setItem(i, { fact_quantity: e.target.value })}
                    disabled={isPosted}
                    style={{ textAlign: 'right' }}
                  />
                </td>
                <td style={{
                  padding: '0.5rem', textAlign: 'right', fontWeight: 600,
                  background: varBg,
                }}>
                  {variance > 0 ? '+' : ''}{fmt(variance)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: '#64748b' }}>
                  {fmtMoney(Number(it.book_amount ?? 0))}
                </td>
                {isPosted && (
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                    {fmtMoney(Number(it.fact_amount ?? 0))}
                  </td>
                )}
                <td style={{ padding: '0.5rem' }}>
                  {!isPosted && (
                    <button
                      onClick={() => removeRow(i)}
                      className="danger"
                      style={{ padding: '0.3rem 0.6rem' }}
                    >×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}
      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          <strong>Як працювати:</strong> 1) Збережіть документ. 2) Натисніть
          «Заповнити з обліку» — система проставить облікове кол-во з регістрів
          на дату інвентаризації. 3) Внесіть фактичні залишки у колонку «Факт».
          4) Натисніть «провести» — для лишків створиться партія
          (з середньозваженою ціною на дату; якщо номенклатури на складі не було —
          0, бухгалтер потім скоригує) і проводка <strong>Дт 281 — Кт 719</strong>;
          для нестач — списання партій за методом організації (FIFO/WAVG)
          і проводка <strong>Дт 947 — Кт 281</strong>.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
